//src/app/features/questionnaires/create/PrepareQuestionnaire/slice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { projectsApi } from 'app/services/projects';
import { questionnairesApi } from 'app/services/questionnaires';

export enum Citizenship {
  US_Citizen = 'US Citizen',
  GC_Holder = 'GC Holder',
  Not_US_Citizen_Not_GC_Holder = 'Not a US Citizen or GC Holder',
}
export enum TaxRequestType {
  Individual = 'individual',
  Business = 'business',
}

export enum SpouseOrDependent {
  Spouse = 'Spouse',
  Dependant = 'Dependent',
}

export enum TaxReturnOwnership {
  PrimaryTaxPayer = 'Primary Tax Payer',
  SpouseOrDependent = 'Spouse or Dependent',
}

export interface TaxRequestTargetState {
  taxRequestType: TaxRequestType.Individual | TaxRequestType.Business | null;
  businessId: string | null;
  specializationId: string | null;
  taxReturnOwnership: string | null;
  spouseOrDependentId: string | null;
}
type TYesOrNo = 'yes' | 'no' | null;
type TNumber = number | null;

export interface PrepareQuestionnaireState {
  taxRequestType: TaxRequestType.Individual | TaxRequestType.Business | null;
  specializationId: string | null;
  taxReturnOwnership: string | null;
  spouseOrDependentId: string | null;
  year: string | null;
  citizenship:
    | Citizenship.US_Citizen
    | Citizenship.GC_Holder
    | Citizenship.Not_US_Citizen_Not_GC_Holder
    | null;
  daysInsideTheUsRange:
    | { from: Nullable<string>; to: Nullable<string> }[]
    | null;
  daysOutsideUs: Nullable<TYesOrNo>;
  daysOutsideUsyear1: number | null;
  daysOutsideUsyear2: number | null;
  daysOutsideUsyear3: number | null;
  businessId: number | null;
  whatCountryAreYouACitizenOf: string | null;

  married: TYesOrNo;
  whatIsYourFillingStatus: TYesOrNo;
  haveChildren: TYesOrNo;
  paidByEmployer: TYesOrNo;
  howManyW2SDoYouHave: TNumber;
  incomeOutsideUs: TYesOrNo;
  incomeFromUs: TYesOrNo;
  financialAccountsOutsideUs: TYesOrNo;
  selfEmployed: TYesOrNo;
  corpOwnership: TYesOrNo;
  ownershipNonUsCorp: TYesOrNo;
  ownershipUsCorp: TYesOrNo;
  usTrus: TYesOrNo;
  nonUsTrust: TYesOrNo;
  investments: TYesOrNo;
  reitermentAccount: TYesOrNo;
  soldProperty: TYesOrNo;
  recieveDividends: TYesOrNo;
  earnInterest: TYesOrNo;
  rentProperty: TYesOrNo;
  spouse: TYesOrNo;
  spousewageIncome: TYesOrNo;
  spousePension: TYesOrNo;
  spouseDivedends: TYesOrNo;
  spouseIncomeInterest: TYesOrNo;
  howManyBusinessesDoYouHaveWhereYouAreTheOnlyMember: TNumber;
  howManyRentalPropertiesDidYouHave: TNumber;
  howManyBrokerageAccountsOrPlatformsDidYouUse: TNumber;
  howMuchDidYouDeposit: TNumber;
  areYouAnActiveMemberOfTheMilitary: TYesOrNo;
  didYouMoveDuringTheYear: TYesOrNo;
  doYouOwnAHome: TYesOrNo;
  didYouPurchaseAHomeInTheYearForThisFiling: TYesOrNo;
  didYouEngageInCryptocurrency: TYesOrNo;
  howManyBrokerageAccountsPlatformsDidYouUse_Cryptocurrency: TNumber;
  doYouHaveAFarm: TYesOrNo;
  doYouHaveBusinessInterestInAPartnershipOrCorporationReceiveAK1Forms: TYesOrNo;
  howManyDifferentBusinessesDoYouOwn: TNumber;
  didYouLiveOrWorkInMoreThanOneState: TYesOrNo;
  howManyStateDidYouLiveOrWorkIn: TNumber;
  didYouReceiveInterestIncome: TYesOrNo;
  howManyForms1099IntDidYouReceive: TNumber;
  howManyBrokerageAccountsOrPlatformsDidYouUse_Investments: TNumber;
  areYouAVeteran: TYesOrNo;

  areAnyOfYourRentalPropertiesOutsideOfTheStateYouLiveIn: TYesOrNo;
  whatStateIsThePropertyIn: string[] | null;
  isAoaSubscriber: TYesOrNo;
  doYouHaveAnyGrossIncomeForThisYearInQuestion: TYesOrNo;
  didYouHaveAnyExpensesForThisYear: TYesOrNo;
  doYouHavePayroll: TYesOrNo;
  payrollReportRequiredToReconcile?: null;
  howManyEmployess: TNumber;
  doYouHaveACheckingBusinessBankAccount: TYesOrNo;
  doesAccountantsOnAirDoYourBookKeepingServices: TYesOrNo;
  doYouHaveAFinancialStatement: TYesOrNo;
  didYouHaveAnyExpensesForThisYearInQuestion: TYesOrNo;
  above10kExpenses: TYesOrNo;
  above10kGrossIncome: TYesOrNo;
}

const initialState: PrepareQuestionnaireState = {
  year: null,
  citizenship: null,
  daysOutsideUs: null,
  daysOutsideUsyear1: null,
  daysOutsideUsyear2: null,
  daysOutsideUsyear3: null,
  whatCountryAreYouACitizenOf: null,
  businessId: null,
  specializationId: null,
  spouseOrDependentId: null,
  taxRequestType: null,
  taxReturnOwnership: TaxReturnOwnership.PrimaryTaxPayer, // Default value,
  daysInsideTheUsRange: [{ from: null, to: null }],
  payrollReportRequiredToReconcile: null,
  married: null,
  whatIsYourFillingStatus: null,
  haveChildren: null,
  paidByEmployer: null,
  howManyW2SDoYouHave: null,
  incomeOutsideUs: null,
  incomeFromUs: null,
  financialAccountsOutsideUs: null,
  selfEmployed: null,
  corpOwnership: null,
  ownershipNonUsCorp: null,
  ownershipUsCorp: null,
  usTrus: null,
  nonUsTrust: null,
  investments: null,
  reitermentAccount: null,
  soldProperty: null,
  recieveDividends: null,
  earnInterest: null,
  rentProperty: null,
  spouse: null,
  spousewageIncome: null,
  spousePension: null,
  spouseDivedends: null,
  spouseIncomeInterest: null,
  howManyRentalPropertiesDidYouHave: null,
  howManyBusinessesDoYouHaveWhereYouAreTheOnlyMember: null,
  howManyBrokerageAccountsOrPlatformsDidYouUse: null,
  howMuchDidYouDeposit: null,
  areYouAnActiveMemberOfTheMilitary: null,
  didYouEngageInCryptocurrency: null,
  didYouLiveOrWorkInMoreThanOneState: null,
  didYouMoveDuringTheYear: null,
  didYouPurchaseAHomeInTheYearForThisFiling: null,
  didYouReceiveInterestIncome: null,
  doYouHaveAFarm: null,
  doYouHaveBusinessInterestInAPartnershipOrCorporationReceiveAK1Forms: null,
  doYouOwnAHome: null,
  howManyDifferentBusinessesDoYouOwn: null,
  howManyForms1099IntDidYouReceive: null,
  howManyStateDidYouLiveOrWorkIn: null,
  howManyBrokerageAccountsPlatformsDidYouUse_Cryptocurrency: null,
  howManyBrokerageAccountsOrPlatformsDidYouUse_Investments: null,
  areYouAVeteran: null,
  whatStateIsThePropertyIn: null,
  areAnyOfYourRentalPropertiesOutsideOfTheStateYouLiveIn: null,
  isAoaSubscriber: null,
  doYouHaveAnyGrossIncomeForThisYearInQuestion: null,
  didYouHaveAnyExpensesForThisYear: null,
  doYouHavePayroll: null,
  howManyEmployess: null,
  doYouHaveACheckingBusinessBankAccount: null,
  doesAccountantsOnAirDoYourBookKeepingServices: null,
  doYouHaveAFinancialStatement: null,
  didYouHaveAnyExpensesForThisYearInQuestion: null,
  above10kExpenses: null,
  above10kGrossIncome: null,
};

const PrepareQuestionnaireSlice = createSlice({
  name: 'prepare_questionnaire',
  initialState,
  reducers: {
    update(state, action: PayloadAction<PrepareQuestionnaireState>) {
      Object.keys(action.payload).forEach((key) => {
        // @ts-ignore
        state[key as string] = action.payload[key];
      });
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof PrepareQuestionnaireState;
        value: any;
      }>,
    ) {
      _.set(state, `${action.payload.key}`, action.payload.value);

      if (action.payload.key === 'taxRequestType') {
        if (action.payload.value === TaxRequestType.Individual) {
          state.businessId = null;
        }
        if (action.payload.value === TaxRequestType.Business) {
          state.specializationId = null;
          state.spouseOrDependentId = null;
          state.taxReturnOwnership = null;
        }
      }

      if (action.payload.key === 'paidByEmployer') {
        if (action.payload.value === 'no') {
          state.incomeFromUs = 'no';
          state.incomeOutsideUs = 'no';
        }
      }
      if (action.payload.key === 'corpOwnership') {
        if (action.payload.value === 'no') {
          state.ownershipNonUsCorp = 'no';
          state.ownershipUsCorp = 'no';
          state.usTrus = 'no';
          state.nonUsTrust = 'no';
        }
      }
      if (action.payload.key === 'spouse') {
        if (action.payload.value === 'no') {
          state.spousewageIncome = 'no';
          state.spousePension = 'no';
          state.spouseDivedends = 'no';
          state.spouseIncomeInterest = 'no';
        }
      }
    },
    updateFieldArrayKey(
      state: any,
      action: PayloadAction<{
        key: keyof PrepareQuestionnaireState | '';
        value: any;
      }>,
    ) {
      const fieldArray =
        action.payload.value &&
        action.payload.value.map(({ ...field }) => ({
          ...field,
        }));

      if (action.payload.key === 'daysInsideTheUsRange')
        state[action.payload.key] = fieldArray;
    },

    reset: () => initialState,
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        const pq = payload.questionnaire?.data?.prepareQuestionnaire;
        if (pq)
          Object.keys(pq).forEach((key) => {
            // @ts-ignore
            state[key as string] = pq[key];
          });
      },
    );
    builder.addMatcher(
      projectsApi.endpoints.getProjectById.matchFulfilled,
      (state, { payload }) => {
        if (payload.project) {
          const pq = payload.project.questionnaire?.data?.prepareQuestionnaire;
          if (pq) {
            Object.keys(pq).forEach((key) => {
              // @ts-ignore
              state[key as string] = pq[key];
            });
          }

          const { businesses, project_beneficiary } = payload.project;
          state.taxRequestType =
            businesses.length === 0
              ? TaxRequestType.Individual
              : TaxRequestType.Business;
          state.businessId = businesses.length === 0 ? null : businesses[0].id;
          state.taxReturnOwnership = project_beneficiary
            ? TaxReturnOwnership.SpouseOrDependent
            : TaxReturnOwnership.PrimaryTaxPayer;
          state.spouseOrDependentId = String(
            project_beneficiary?.beneficiary?.id,
          );
        }
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey, reset } =
  PrepareQuestionnaireSlice.actions;
export default PrepareQuestionnaireSlice.reducer;

export const selectPrepareQuestionnaire = (state: RootState) =>
  state.prepareQuestionnaire;
